import { SubStepTypes, type WorkflowCompletion } from '@generated/graphql';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import type { DepositReviewSubstepOptions } from './depositReview.visual';

// Order is preference, not workflow position. DEPOSIT_FORM is the fallback for
// workflows that set `defaultTransferType` and omit the DEPOSIT_TYPE picker (OVT-6415).
export const ADD_ANOTHER_DEPOSIT_TARGET_TYPES: readonly SubStepTypes[] = [
  SubStepTypes.DepositType,
  SubStepTypes.DepositForm,
];

export const findAddAnotherDepositTargetSubStep = (
  workflowCompletion?: Pick<WorkflowCompletion, 'workflow'>,
  targetTypes: readonly SubStepTypes[] = ADD_ANOTHER_DEPOSIT_TARGET_TYPES,
): { id?: string; type?: string } | undefined => {
  const subSteps = (workflowCompletion?.workflow?.steps ?? []).flatMap(
    (step) => step?.subSteps ?? [],
  );
  for (const type of targetTypes) {
    const match = subSteps.find((subStep) => subStep?.type === type);
    if (match) return match;
  }
};

export const resolveDepositReviewActions = (
  options: DepositReviewSubstepOptions,
) => {
  const multipleDepositsEnabled = options.enableMultipleDeposits === true;
  const hasEditWorkflow =
    Boolean(options.depositEditWorkflow) &&
    options.depositEditWorkflow !== 'default';
  return {
    canAddAnother:
      multipleDepositsEnabled && options.enableAddAnotherDeposit === true,
    canDelete: multipleDepositsEnabled && options.enableDepositDelete === true,
    canEdit:
      multipleDepositsEnabled &&
      options.enableDepositEdit === true &&
      hasEditWorkflow,
  };
};

export const isFullInternalTransfer = (deposit: TransferContextData) => {
  const marketValueCents = deposit.fromSubAccount?.statistics?.marketValueCents;
  if (!marketValueCents) return false;
  return (deposit.amountCents ?? 0) > marketValueCents * 0.97;
};

export const applySubmissionResults = ({
  deposits,
  failedTemporaryIds,
  referenceIds,
}: {
  deposits: TransferContextData[];
  failedTemporaryIds: string[];
  referenceIds: Record<string, string | undefined>;
}) =>
  deposits.map((deposit): TransferContextData => {
    if (deposit.temporaryTransferState === 'completed') return deposit;
    const temporaryId = deposit.temporaryId ?? '';
    return {
      ...deposit,
      temporaryTransferState: failedTemporaryIds.includes(temporaryId)
        ? 'failed'
        : 'completed',
      referenceId: referenceIds[temporaryId],
    };
  });

export const resolveDepositReviewSubmitLabelKey = (
  mode: 'create' | 'edit',
  deposits: TransferContextData[],
) => {
  if (mode === 'edit') return 'depositReview.updateDeposit';
  if (deposits.every((d) => d.temporaryTransferState === 'completed'))
    return 'depositReview.continue';
  return 'depositReview.createDeposit';
};
